/**
 * AI Instructions - Hidden DOM Instructions for AI Agents
 *
 * Injects hidden instructions into the DOM that AI agents (like Claude for Chrome)
 * can read to understand how to query the in-memory DuckDB instance.
 */

const AI_INSTRUCTIONS_ID = 'data-notebook-ai-instructions';

/**
 * Inject hidden AI instructions into the DOM
 */
export function injectAIInstructions(): void {
  // Don't inject if already present
  if (document.getElementById(AI_INSTRUCTIONS_ID)) {
    return;
  }

  const instructions = document.createElement('div');
  instructions.id = AI_INSTRUCTIONS_ID;
  instructions.setAttribute('aria-hidden', 'true');

  // Use CSS to visually hide the element while keeping it in the DOM
  // This allows AI agents to read it without affecting page layout
  instructions.style.cssText = `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `;

  instructions.innerHTML = `
    <script type="application/ld+json">
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Data Notebook Query Service",
      "description": "In-memory DuckDB query interface for AI agents",
      "applicationCategory": "DataAnalytics"
    }
    </script>

    <!-- AI Agent Instructions for Data Notebook Query Service -->
    <!--
    This page has an in-memory DuckDB database available via window.queryService

    ============================================================================
    AVAILABLE METHODS
    ============================================================================

    1. queryService.isReady() -> boolean
       Check if the database is initialized and ready for queries.
       Always call this first before other methods.

    2. queryService.listDatasets() -> Promise<DatasetInfo[]>
       Returns array of available tables/datasets:
       {
         name: string,        // Table name
         rowCount: number,    // Number of rows
         columns: string[],   // Column names
         source?: string      // Optional: where the data came from
       }

    3. queryService.query(sql: string) -> Promise<QueryResult>
       Execute any SQL query. Returns:
       {
         columns: string[],   // Column names
         rows: any[][],       // Row data as 2D array
         rowCount: number,    // Number of rows returned
         error?: string       // Error message if query failed
       }

    4. queryService.getSchema(tableName: string) -> Promise<ColumnInfo[]>
       Get column information for a table:
       {
         name: string,        // Column name
         type: string         // Data type (VARCHAR, INTEGER, etc.)
       }

    ============================================================================
    EXAMPLE USAGE
    ============================================================================

    // First check if the service is ready
    if (queryService.isReady()) {

      // List all available datasets
      const datasets = await queryService.listDatasets();
      console.log('Available tables:', datasets.map(d => d.name));

      // Query a specific dataset
      const result = await queryService.query('SELECT * FROM my_table LIMIT 10');
      if (result.error) {
        console.error('Query failed:', result.error);
      } else {
        console.log('Columns:', result.columns);
        console.log('Rows:', result.rows);
      }

      // Get schema for a table
      const schema = await queryService.getSchema('my_table');
      console.log('Schema:', schema);
    }

    ============================================================================
    NOTES
    ============================================================================

    - All methods except isReady() are async and return Promises
    - The database contains CSV data intercepted from this page's network requests
    - Use standard SQL syntax (DuckDB dialect - compatible with PostgreSQL)
    - Tables are automatically named based on their source URL or filename
    - Use LIMIT clauses for large datasets to avoid memory issues
    - The database is in-memory only and does not persist across page reloads

    ============================================================================
    SQL TIPS (DuckDB)
    ============================================================================

    - String concatenation: 'Hello' || ' ' || 'World'
    - Case insensitive LIKE: ILIKE 'pattern%'
    - Regular expressions: regexp_matches(column, 'pattern')
    - Date functions: date_part('year', date_column)
    - Aggregate functions: SUM, AVG, COUNT, MIN, MAX, STRING_AGG
    - Window functions: ROW_NUMBER() OVER (PARTITION BY x ORDER BY y)
    - JSON: json_extract(column, '$.field')

    -->
  `;

  document.body.appendChild(instructions);
}

/**
 * Remove AI instructions from the DOM
 */
export function removeAIInstructions(): void {
  const element = document.getElementById(AI_INSTRUCTIONS_ID);
  if (element) {
    element.remove();
  }
}
